import { createRouter, createWebHistory } from 'vue-router';

import { frontendURL } from '../helper/URLHelper';
import dashboard from './dashboard/dashboard.routes';
import store from 'dashboard/store';
import { validateLoggedInRoutes } from '../helper/routeHelpers';
import { isOnOnboardingView } from 'v3/helpers/RouteHelper';
import { isLocalAuthPreview } from 'shared/helpers/localAuthPreview';
import { WEB_ORDER_ACCESS_PERMISSIONS } from 'dashboard/constants/permissions';
import AnalyticsHelper from '../helper/AnalyticsHelper';
import { SESSION_STORAGE_KEYS } from 'dashboard/constants/sessionStorage';
import SessionStorage from 'shared/helpers/sessionStorage';

const ONBOARDING_STEPS = ['account_details', 'enrichment', 'inbox_setup'];
const routes = [...dashboard.routes];

const onboardingPath = step =>
  step === 'inbox_setup' ? 'onboarding/inbox-setup' : 'onboarding';

export const router = createRouter({ history: createWebHistory(), routes });

const CHUNK_RELOAD_KEY = 'wacord:chunk-reload';

router.onError((error, to) => {
  const message = String(error?.message || '');
  const chunkMissing =
    /dynamically imported module|Importing a module script failed|error loading dynamically imported module/i.test(
      message
    );
  if (!chunkMissing || !to?.fullPath) return;

  const previous = sessionStorage.getItem(CHUNK_RELOAD_KEY);
  if (previous === to.fullPath) return;

  sessionStorage.setItem(CHUNK_RELOAD_KEY, to.fullPath);
  window.location.assign(to.fullPath);
});

export const validateAuthenticateRoutePermission = async (to, next) => {
  const { isLoggedIn, getCurrentUser: user } = store.getters;

  if (!isLoggedIn) {
    const lastAuthGate = SessionStorage.get(
      SESSION_STORAGE_KEYS.LAST_AUTH_GATE
    );
    if (isLocalAuthPreview()) {
      window.location.assign(
        lastAuthGate === 'agent' ? '/app/login/agent' : '/app/login'
      );
      return '';
    }
    window.location.assign(
      lastAuthGate === 'agent'
        ? '/auth.html?mode=agent&error=session'
        : '/auth.html?mode=login&error=session'
    );
    return '';
  }

  const { accounts = [], account_id: accountId } = user;

  if (!accounts.length) {
    if (to.name === 'no_accounts') {
      return next();
    }
    return next(frontendURL('no-accounts'));
  }

  const routeAccountId = Number(to.params?.accountId || accountId);
  const userAccount = accounts.find(a => a.id === routeAccountId);
  const isAdmin = userAccount?.role === 'administrator';
  const isActive = userAccount?.status === 'active';
  const needsOnboarding =
    ONBOARDING_STEPS.includes(userAccount?.onboarding_step) &&
    isAdmin &&
    isActive;

  if (to.name === 'no_accounts' || !to.name) {
    const agentGate = SessionStorage.get(SESSION_STORAGE_KEYS.AGENT_LOGIN_GATE);
    if (agentGate && !needsOnboarding) {
      SessionStorage.remove(SESSION_STORAGE_KEYS.AGENT_LOGIN_GATE);
      const permissions = userAccount?.permissions || [];
      const canWebOrder =
        isAdmin ||
        permissions.includes('administrator') ||
        WEB_ORDER_ACCESS_PERMISSIONS.some(permission =>
          permissions.includes(permission)
        );
      if (canWebOrder) {
        return next(frontendURL(`accounts/${routeAccountId}/web-order/orders`));
      }
    }
    const target = needsOnboarding
      ? onboardingPath(userAccount?.onboarding_step)
      : 'dashboard';
    return next(frontendURL(`accounts/${routeAccountId}/${target}`));
  }

  if (needsOnboarding && !isOnOnboardingView(to)) {
    return next(
      frontendURL(
        `accounts/${routeAccountId}/${onboardingPath(userAccount?.onboarding_step)}`
      )
    );
  }
  if (!needsOnboarding && isOnOnboardingView(to)) {
    return next(frontendURL(`accounts/${routeAccountId}/dashboard`));
  }

  const nextRoute = validateLoggedInRoutes(to, store.getters.getCurrentUser);
  return nextRoute ? next(frontendURL(nextRoute)) : next();
};

export const initalizeRouter = () => {
  const userAuthentication = store.dispatch('setUser');

  router.beforeEach(async (to, _from, next) => {
    AnalyticsHelper.page(to.name || '', {
      path: to.path,
      name: to.name,
    });

    await userAuthentication;
    await validateAuthenticateRoutePermission(to, next, store);
  });
};

export default router;
